<template>
  <div class="p-4 md:p-6">
    <!-- Encabezado -->
    <div class="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
      <div>
        <h1 class="text-2xl font-bold text-gray-800 dark:text-white">Gestión de Usuarios</h1>
        <p class="text-sm md:text-base text-gray-600 dark:text-gray-400">Administra los usuarios del sistema</p>
      </div>
      <div class="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full md:w-auto">
        <!-- SELECTOR DE REPORTE POR ROL -->
        <div class="relative group w-full sm:w-64">
          <select
            v-model="selectedRolReporte"
            @change="generarReportePorRol"
            class="w-full appearance-none px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-10 transition-all duration-300 text-sm"
          >
            <option value="">📊 Reporte por Rol...</option>
            <option v-for="rol in roles" :key="rol.idRol" :value="rol.idRol">
              {{ rol.nombre }}
            </option>
          </select>
          <Icon icon="mdi:chevron-down" class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
        </div>

        <!-- BOTÓN DE REPORTE GENERAL -->
        <button
          @click="generarReporteGeneral"
          class="flex items-center justify-center px-3 py-2 md:px-4 md:py-2.5 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 transition-all duration-300 shadow-md hover:shadow-lg text-sm md:text-base"
        >
          <Icon icon="mdi:file-document" class="mr-2 text-lg" />
          <span class="hidden sm:inline">Reporte General</span>
          <span class="sm:hidden">Reporte</span>
        </button>

        <!-- BOTÓN NUEVO USUARIO -->
        <button
          @click="openCreateModal"
          class="flex items-center justify-center px-3 py-2 md:px-4 md:py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 shadow-md hover:shadow-lg text-sm md:text-base"
        >
          <Icon icon="mdi:plus" class="mr-2 text-lg" />
          <span class="hidden sm:inline">Nuevo Usuario</span>
          <span class="sm:hidden">Nuevo</span>
        </button>
      </div>
    </div>

    <!-- Filtros y búsqueda -->
    <div class="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-3 mb-4 border border-gray-200 dark:border-gray-700">
      <div class="grid grid-cols-12 gap-2">
        <div class="col-span-12 md:col-span-4">
          <label class="block text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Buscar Usuarios</label>
          <div class="relative">
            <Icon icon="mdi:magnify" class="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            <input v-model="searchQuery" type="text" placeholder="Nombre, usuario, email, teléfono..."
              class="w-full pl-10 pr-3 py-1.5 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs transition-all"
              @input="handleSearch" />
          </div>
        </div>
        <div class="col-span-6 md:col-span-2">
          <label class="block text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Estado</label>
          <div class="relative">
            <select v-model="filterStatus" @change="handleFilterChange"
              class="w-full appearance-none border border-gray-200 dark:border-gray-600 rounded-lg px-2.5 py-1.5 bg-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 pr-7 text-xs transition-all">
              <option value="all">Todos</option>
              <option value="activos">Activos</option>
              <option value="inactivos">Inactivos</option>
              <option value="bloqueados">Bloqueados</option>
            </select>
            <Icon icon="mdi:chevron-down" class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none text-sm" />
          </div>
        </div>
        <div class="col-span-6 md:col-span-2">
          <label class="block text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Rol</label>
          <div class="relative">
            <select v-model="filterRol"
              class="w-full appearance-none border border-gray-200 dark:border-gray-600 rounded-lg px-2.5 py-1.5 bg-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 pr-7 text-xs transition-all">
              <option value="all">Todos</option>
              <option v-for="rol in roles" :key="rol.idRol" :value="rol.idRol">{{ rol.nombre }}</option>
            </select>
            <Icon icon="mdi:chevron-down" class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none text-sm" />
          </div>
        </div>
        <div class="col-span-6 md:col-span-2">
          <label class="block text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Tipo</label>
          <div class="relative">
            <select v-model="filterTipo"
              class="w-full appearance-none border border-gray-200 dark:border-gray-600 rounded-lg px-2.5 py-1.5 bg-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 pr-7 text-xs transition-all">
              <option value="all">Todos</option>
              <option value="tecnicos">Técnicos</option>
              <option value="no_tecnicos">No Técnicos</option>
            </select>
            <Icon icon="mdi:chevron-down" class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none text-sm" />
          </div>
        </div>
        <div class="col-span-6 md:col-span-2">
          <label class="block text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Ordenar</label>
          <div class="relative">
            <select v-model="sortBy"
              class="w-full appearance-none border border-gray-200 dark:border-gray-600 rounded-lg px-2.5 py-1.5 bg-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 pr-7 text-xs transition-all">
              <option value="nombre">Nombre (A-Z)</option>
              <option value="nombre_desc">Nombre (Z-A)</option>
              <option value="nombreUsuario">Usuario (A-Z)</option>
              <option value="email">Email (A-Z)</option>
              <option value="rol">Rol</option>
              <option value="fechaRegistro_desc">Más recientes</option>
              <option value="intentos_desc">Más intentos</option>
            </select>
            <Icon icon="mdi:sort" class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none text-sm" />
          </div>
        </div>
      </div>

      <!-- BOTONES DE REPORTES POR FILTRO -->
      <div class="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 flex flex-wrap gap-2">
        <span class="text-sm font-medium text-gray-700 dark:text-gray-300 mr-2 py-2">Reportes rápidos:</span>
        
        <button
          @click="generarReportePorFiltro('activos', 'USUARIOS ACTIVOS')"
          class="px-3 py-1.5 bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-lg text-xs font-medium hover:bg-green-100 dark:hover:bg-green-800/50 transition-colors border border-green-200 dark:border-green-800"
        >
          <Icon icon="mdi:check-circle" class="mr-1 inline" /> Activos
        </button>
        
        <button
          @click="generarReportePorFiltro('inactivos', 'USUARIOS INACTIVOS')"
          class="px-3 py-1.5 bg-gray-50 dark:bg-gray-900/30 text-gray-700 dark:text-gray-300 rounded-lg text-xs font-medium hover:bg-gray-100 dark:hover:bg-gray-800/50 transition-colors border border-gray-300 dark:border-gray-700"
        >
          <Icon icon="mdi:close-circle" class="mr-1 inline" /> Inactivos
        </button>
        
        <button
          @click="generarReportePorFiltro('bloqueados', 'USUARIOS BLOQUEADOS')"
          class="px-3 py-1.5 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-lg text-xs font-medium hover:bg-red-100 dark:hover:bg-red-800/50 transition-colors border border-red-200 dark:border-red-800"
        >
          <Icon icon="mdi:block-helper" class="mr-1 inline" /> Bloqueados
        </button>
        
        <button
          @click="generarReportePorFiltro('tecnicos', 'TÉCNICOS')"
          class="px-3 py-1.5 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-lg text-xs font-medium hover:bg-blue-100 dark:hover:bg-blue-800/50 transition-colors border border-blue-200 dark:border-blue-800"
        >
          <Icon icon="mdi:wrench" class="mr-1 inline" /> Técnicos
        </button>
        
        <button
          @click="generarReportePorFiltro('con_intentos', 'CON INTENTOS FALLIDOS')"
          class="px-3 py-1.5 bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded-lg text-xs font-medium hover:bg-amber-100 dark:hover:bg-amber-800/50 transition-colors border border-amber-200 dark:border-amber-800"
        >
          <Icon icon="mdi:alert" class="mr-1 inline" /> Con Intentos
        </button>
      </div>
    </div>

    <!-- GRID DE USUARIOS -->
    <div class="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden border border-gray-200 dark:border-gray-700">
      <div class="px-4 md:px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
        <div>
          <h2 class="text-base md:text-lg font-semibold text-gray-800 dark:text-white">Lista de Usuarios</h2>
          <p class="text-xs md:text-sm text-gray-600 dark:text-gray-400">
            Mostrando {{ Math.min(paginatedUsuarios.length, itemsPerPage) }} de {{ filteredUsuarios.length }} usuarios
          </p>
        </div>
        <div class="flex items-center space-x-2 text-xs md:text-sm text-gray-500 dark:text-gray-400">
          <Icon icon="mdi:calendar" class="inline mr-1 text-sm" />
          <span class="hidden xs:inline">Última actualización:</span> {{ formatTime(lastUpdate) }}
        </div>
      </div>

      <div v-if="loading" class="py-12 md:py-16">
        <div class="flex flex-col items-center justify-center">
          <div class="animate-spin rounded-full h-10 w-10 md:h-12 md:w-12 border-t-2 border-b-2 border-blue-600 mb-4"></div>
          <p class="text-sm md:text-base text-gray-600 dark:text-gray-400">Cargando usuarios...</p>
        </div>
      </div>

      <div v-else-if="filteredUsuarios.length === 0" class="py-12 md:py-16 px-4">
        <div class="flex flex-col items-center text-center">
          <div class="p-4 bg-gray-100 dark:bg-gray-700 rounded-full mb-4">
            <Icon icon="mdi:account-off" class="text-3xl md:text-4xl text-gray-400" />
          </div>
          <h3 class="text-base md:text-lg font-medium text-gray-800 dark:text-white mb-2">
            No se encontraron usuarios
          </h3>
          <p class="text-xs md:text-sm text-gray-600 dark:text-gray-400 mb-6 max-w-md">
            {{ searchQuery || filterStatus !== 'all' || filterRol !== 'all' || filterTipo !== 'all'
              ? 'Intenta con otros términos de búsqueda o cambia los filtros.' 
              : 'No hay usuarios registrados. ¡Registra el primero!' }}
          </p>
          <button
            v-if="!searchQuery && filterStatus === 'all' && filterRol === 'all' && filterTipo === 'all'"
            @click="openCreateModal"
            class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
          >
            <Icon icon="mdi:plus" class="mr-2 inline" />
            Registrar primer usuario
          </button>
          <button
            v-else
            @click="clearFilters"
            class="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors text-sm"
          >
            <Icon icon="mdi:filter-off" class="mr-2 inline" />
            Limpiar filtros
          </button>
        </div>
      </div>

      <div v-else class="p-4 md:p-6">
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          <div 
            v-for="usuario in paginatedUsuarios" 
            :key="usuario.idUsuario"
            class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden"
          >
            <div class="relative h-32 sm:h-40 md:h-48 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 flex items-center justify-center">
              <!-- Estado clickeable -->
              <span
                @click="abrirModalToggleStatus(usuario)"
                :class="[
                  'absolute top-2 right-2 z-10 px-2 py-1 text-xs font-semibold rounded-full shadow-sm cursor-pointer transition-all duration-200 hover:scale-105',
                  getStatusClass(usuario.estado)
                ]"
              >
                <Icon :icon="getStatusIcon(usuario.estado)" class="mr-1 inline text-xs" />
                {{ convertirEstadoATexto(usuario.estado) }}
              </span>

              <!-- Avatar o Foto -->
              <div class="h-full w-full flex items-center justify-center p-4">
                <img 
                  v-if="getImageUrl(usuario.fotoPerfil)"
                  :src="getImageUrl(usuario.fotoPerfil)"
                  :alt="usuario.nombre"
                  class="max-h-full max-w-full object-contain rounded-full"
                  @error="handleImageError"
                />
                <div 
                  v-else
                  class="w-20 h-20 md:w-24 md:h-24 rounded-full flex items-center justify-center text-white text-2xl md:text-3xl font-bold shadow-lg"
                  :style="{ backgroundColor: obtenerColorAvatar(usuario.nombre + ' ' + usuario.apellido) }"
                >
                  {{ obtenerIniciales(usuario) }}
                </div>
              </div>

              <!-- Badge de técnico si aplica -->
              <div v-if="usuario.tipo_tecnico" class="absolute bottom-2 left-2">
                <span class="px-2 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 text-xs font-semibold rounded-full shadow-sm">
                  <Icon icon="mdi:wrench" class="inline mr-1 text-xs" />
                  Técnico
                </span>
              </div>
            </div>

            <div class="p-3 md:p-4">
              <div class="flex flex-wrap items-center gap-2 mb-2">
                <span class="text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-1 rounded-full">
                  <Icon icon="mdi:account" class="inline mr-1 text-xs" />
                  @{{ usuario.nombreUsuario }}
                </span>
                <span class="text-xs bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 px-2 py-1 rounded-full truncate max-w-[120px]">
                  <Icon icon="mdi:badge-account" class="inline mr-1 text-xs" />
                  {{ usuario.nombreRol || 'Sin rol' }}
                </span>
              </div>

              <h3 class="text-sm md:text-base font-semibold text-gray-900 dark:text-white mb-1 line-clamp-2">
                {{ obtenerNombreCompleto(usuario) }}
              </h3>
              <p class="text-xs text-gray-500 dark:text-gray-400 mb-3 flex items-center">
                <Icon icon="mdi:email" class="mr-1 text-xs" />
                {{ usuario.email }}
              </p>

              <div class="space-y-2 mb-4">
                <div class="flex justify-between items-center text-sm">
                  <span class="text-gray-600 dark:text-gray-400">Teléfono:</span>
                  <span class="font-semibold">{{ formatearTelefono(usuario.telefono) }}</span>
                </div>
                
                <div v-if="usuario.tipo_tecnico" class="flex justify-between items-center text-xs">
                  <span class="text-gray-600 dark:text-gray-400">Especialidad:</span>
                  <span class="font-semibold text-blue-600 dark:text-blue-400">{{ usuario.especialidad || 'N/A' }}</span>
                </div>
                
                <div v-if="usuario.tipo_tecnico && usuario.comision_porcentaje > 0" class="flex justify-between items-center text-xs">
                  <span class="text-gray-600 dark:text-gray-400">Comisión:</span>
                  <span class="font-semibold text-green-600 dark:text-green-400">{{ usuario.comision_porcentaje }}%</span>
                </div>

                <div class="flex justify-between items-center text-xs pt-2 border-t border-gray-200 dark:border-gray-700">
                  <span class="text-gray-600 dark:text-gray-400">Intentos fallidos:</span>
                  <span :class="[
                    'font-semibold',
                    usuario.intento_login > 3 ? 'text-red-600 dark:text-red-400' : 'text-gray-700 dark:text-gray-300'
                  ]">
                    {{ usuario.intento_login || 0 }}
                  </span>
                </div>
              </div>

              <div class="space-y-1 text-xs text-gray-500 dark:text-gray-400 mb-4">
                <div class="flex items-center">
                  <Icon icon="mdi:calendar-plus" class="mr-1.5 text-sm" />
                  <span>Registro: {{ formatDate(usuario.fechaRegistro, false) }}</span>
                </div>
                <div class="flex items-center">
                  <Icon icon="mdi:clock" class="mr-1.5 text-sm" />
                  <span>Último acceso: {{ formatDate(usuario.ultimo_acceso, true) || 'Nunca' }}</span>
                </div>
              </div>

              <div class="flex items-center justify-between pt-3 border-t border-gray-200 dark:border-gray-700">
                <div class="flex items-center space-x-1">
                  <button
                    @click.stop="openEditModal(usuario)"
                    class="p-2 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-800/50 transition-colors"
                    title="Editar"
                  >
                    <Icon icon="mdi:pencil" class="text-base md:text-lg" />
                  </button>
                  <button
                    @click.stop="abrirModalCambiarPassword(usuario)"
                    class="p-2 bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-800/50 transition-colors"
                    title="Cambiar Contraseña"
                  >
                    <Icon icon="mdi:key" class="text-base md:text-lg" />
                  </button>
                  <button
                    v-if="usuario.intento_login > 0"
                    @click.stop="abrirModalResetearIntentos(usuario)"
                    class="p-2 bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-lg hover:bg-green-100 dark:hover:bg-green-800/50 transition-colors"
                    title="Resetear Intentos"
                  >
                    <Icon icon="mdi:refresh" class="text-base md:text-lg" />
                  </button>
                  <button
                    @click.stop="abrirModalEliminar(usuario)"
                    class="p-2 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-100 dark:hover:bg-red-800/50 transition-colors"
                    title="Eliminar"
                  >
                    <Icon icon="mdi:delete" class="text-base md:text-lg" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Paginación Premium -->
        <div v-if="filteredUsuarios.length > itemsPerPage" class="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div class="text-xs text-gray-500 dark:text-gray-400 order-2 sm:order-1">
            Mostrando <span class="font-semibold text-gray-700 dark:text-gray-300">{{ startItem }}–{{ Math.min(endItem, filteredUsuarios.length) }}</span> de <span class="font-semibold text-gray-700 dark:text-gray-300">{{ filteredUsuarios.length }}</span>
          </div>
          <div class="flex items-center gap-1 order-1 sm:order-2">
            <button @click="currentPage = Math.max(currentPage - 1, 1)" :disabled="currentPage === 1"
              class="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 dark:border-gray-600 text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all">
              <Icon icon="mdi:chevron-left" class="text-base" />
            </button>
            <button v-for="page in visiblePages" :key="page" @click="currentPage = page"
              :class="['w-8 h-8 flex items-center justify-center rounded-lg text-xs font-semibold transition-all',
                currentPage === page
                  ? 'bg-blue-600 text-white shadow-sm shadow-blue-300 dark:shadow-blue-900'
                  : 'border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700']">{{ page }}</button>
            <button @click="currentPage = Math.min(currentPage + 1, totalPages)" :disabled="currentPage === totalPages"
              class="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 dark:border-gray-600 text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all">
              <Icon icon="mdi:chevron-right" class="text-base" />
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ========== MODALES ========== -->

    <!-- MODAL PARA CREAR/EDITAR USUARIO -->
    <Teleport to="body">
    <div v-if="showModal" class="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-[90] backdrop-blur-[2px]">
      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden mx-auto my-auto">
        <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div class="flex items-center justify-between">
            <div class="flex items-center">
              <div class="p-2 rounded-xl bg-gradient-to-r from-purple-500 to-indigo-500 shadow-md mr-3">
                <Icon :icon="editingUsuario ? 'mdi:pencil-box' : 'mdi:account-plus'" class="text-white text-xl" />
              </div>
              <div>
                <h3 class="text-lg font-bold text-gray-900 dark:text-white">
                  {{ editingUsuario ? 'Editar Usuario' : 'Nuevo Usuario' }}
                </h3>
                <p class="text-xs text-gray-600 dark:text-gray-400">
                  {{ editingUsuario ? 'Modifica los datos del usuario' : 'Completa los datos para registrar un nuevo usuario' }}
                </p>
              </div>
            </div>
            <button 
              @click="closeModal"
              class="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <Icon icon="mdi:close" class="text-xl" />
            </button>
          </div>
        </div>
        
        <form @submit.prevent="saveUsuario" class="px-6 py-4 overflow-y-auto max-h-[calc(90vh-130px)]">
          <div class="space-y-4">
            <!-- FOTO DE PERFIL -->
            <div class="flex flex-col items-center mb-6">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Foto de Perfil
              </label>
              <div class="relative group">
                <div 
                  class="w-24 h-24 md:w-32 md:h-32 rounded-full overflow-hidden border-4 border-gray-200 dark:border-gray-700 shadow-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center cursor-pointer"
                  @click="triggerFileInput"
                >
                  <img v-if="previewUrl" :src="previewUrl" class="w-full h-full object-cover" />
                  <Icon v-else icon="mdi:account-circle" class="text-6xl md:text-8xl text-gray-400" />
                  
                  <div class="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <Icon icon="mdi:camera" class="text-white text-2xl" />
                  </div>
                </div>
                
                <button 
                  v-if="previewUrl" 
                  type="button"
                  @click="removeSelectedFile"
                  class="absolute -top-1 -right-1 bg-red-500 text-white p-1 rounded-full shadow-md hover:bg-red-600 transition-colors"
                  title="Quitar foto"
                >
                  <Icon icon="mdi:close" class="text-xs" />
                </button>
              </div>
              <input 
                ref="fileInput" 
                type="file" 
                class="hidden" 
                accept="image/*" 
                @change="handleFileChange" 
              />
              <p class="text-[10px] text-gray-500 mt-2">Formatos: JPG, PNG, WEBP (Máx. 5MB)</p>
            </div>

            <!-- NOMBRE Y APELLIDO -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Nombre <span class="text-red-500">*</span>
                </label>
                <input
                  v-model="form.nombre"
                  type="text"
                  required
                  maxlength="50"
                  class="w-full px-4 py-2.5 border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none transition-all duration-300 text-sm"
                  placeholder="Ej: Juan"
                  @blur="form.nombre = toTitleCase(form.nombre)"
                />
              </div>
              
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Apellido <span class="text-red-500">*</span>
                </label>
                <input
                  v-model="form.apellido"
                  type="text"
                  required
                  maxlength="50"
                  class="w-full px-4 py-2.5 border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none transition-all duration-300 text-sm"
                  placeholder="Ej: Pérez"
                  @blur="form.apellido = toTitleCase(form.apellido)"
                />
              </div>
            </div>
            
            <!-- EMAIL Y TELÉFONO -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email <span class="text-red-500">*</span>
                </label>
                <input
                  v-model="form.email"
                  type="email"
                  required
                  maxlength="100"
                  class="w-full px-4 py-2.5 border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none transition-all duration-300 text-sm"
                  placeholder="Ej: juan@email.com"
                  @blur="verificarEmailUnico"
                />
                <p v-if="errors.email" class="mt-1 text-xs text-red-600">{{ errors.email }}</p>
              </div>
              
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Teléfono
                </label>
                <input
                  v-model="form.telefono"
                  type="text"
                  maxlength="20"
                  class="w-full px-4 py-2.5 border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none transition-all duration-300 text-sm"
                  placeholder="Ej: 04121234567"
                />
              </div>
            </div>
            
            <!-- NOMBRE USUARIO Y CONTRASEÑA (solo si es nuevo) -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Nombre de Usuario <span class="text-red-500">*</span>
                </label>
                <input
                  v-model="form.nombreUsuario"
                  type="text"
                  required
                  maxlength="50"
                  class="w-full px-4 py-2.5 border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none transition-all duration-300 text-sm"
                  placeholder="Ej: jperez"
                  @blur="verificarUsernameUnico"
                />
                <p v-if="errors.nombreUsuario" class="mt-1 text-xs text-red-600">{{ errors.nombreUsuario }}</p>
              </div>
              
              <div v-if="!editingUsuario">
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Contraseña <span class="text-red-500">*</span>
                </label>
                <input
                  v-model="form.contrasena"
                  type="password"
                  required
                  minlength="6"
                  class="w-full px-4 py-2.5 border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none transition-all duration-300 text-sm"
                  placeholder="Mínimo 6 caracteres"
                />
              </div>
            </div>
            
            <!-- ROL -->
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Rol <span class="text-red-500">*</span>
              </label>
              <select
                v-model="form.idRol"
                required
                class="w-full px-4 py-2.5 border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none transition-all duration-300 text-sm"
              >
                <option value="">Seleccionar rol...</option>
                <option v-for="rol in roles" :key="rol.idRol" :value="rol.idRol">
                  {{ rol.nombre }}
                </option>
              </select>
              <p v-if="errors.idRol" class="mt-1 text-xs text-red-600">{{ errors.idRol }}</p>
            </div>
            
            <!-- CAMPOS DE TÉCNICO (condicionales) -->
            <div v-if="esTecnico" class="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
              <h4 class="text-sm font-semibold text-blue-800 dark:text-blue-300 mb-3 flex items-center">
                <Icon icon="mdi:wrench" class="mr-2" />
                Configuración de Técnico
              </h4>
              
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Especialidad
                  </label>
                  <input
                    v-model="form.especialidad"
                    type="text"
                    maxlength="100"
                    class="w-full px-4 py-2.5 border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none transition-all duration-300 text-sm"
                    placeholder="Ej: Reparación de Laptops"
                  />
                </div>
                
                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Comisión (%)
                  </label>
                  <input
                    v-model.number="form.comision_porcentaje"
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    class="w-full px-4 py-2.5 border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none transition-all duration-300 text-sm"
                    placeholder="Ej: 15"
                  />
                </div>
              </div>
            </div>
            
            <!-- CHECKBOX DE TÉCNICO -->
            <div class="flex items-center">
              <input
                type="checkbox"
                id="tipo_tecnico"
                v-model="form.tipo_tecnico"
                class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
              />
              <label for="tipo_tecnico" class="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                Es técnico
              </label>
            </div>
            
            <!-- ESTADO -->
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Estado
              </label>
              <div class="flex space-x-4">
                <label class="flex items-center">
                  <input type="radio" v-model="form.estado" value="Activo" class="mr-2">
                  <span class="text-sm">Activo</span>
                </label>
                <label class="flex items-center">
                  <input type="radio" v-model="form.estado" value="Inactivo" class="mr-2">
                  <span class="text-sm">Inactivo</span>
                </label>
                <label class="flex items-center">
                  <input type="radio" v-model="form.estado" value="Bloqueado" class="mr-2">
                  <span class="text-sm">Bloqueado</span>
                </label>
              </div>
            </div>
          </div>
          
          <div class="mt-6 flex justify-end space-x-3">
            <button
              type="button"
              @click="closeModal"
              class="px-4 py-2.5 border-2 border-gray-300 dark:border-gray-600 rounded-xl text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-300 text-sm"
            >
              Cancelar
            </button>
            <button
              type="submit"
              :disabled="saving || !isFormValid"
              class="px-4 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-medium hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              <Icon v-if="saving" icon="mdi:loading" class="animate-spin mr-2 inline" />
              {{ saving ? 'Guardando...' : (editingUsuario ? 'Actualizar Usuario' : 'Crear Usuario') }}
            </button>
          </div>
        </form>
      </div>
    </div>
    </Teleport>

    <!-- MODAL PARA CAMBIAR CONTRASEÑA -->
    <Teleport to="body">
    <div v-if="showPasswordModal" class="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-[90] backdrop-blur-[2px]">
      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md transform transition-all duration-300 scale-100 mx-auto my-auto">
        <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-amber-600 to-orange-600">
          <div class="flex items-center justify-between">
            <div class="flex items-center text-white">
              <Icon icon="mdi:key" class="text-2xl mr-3" />
              <div>
                <h3 class="text-lg font-bold">Cambiar Contraseña</h3>
                <p class="text-xs text-amber-100">{{ passwordUsuario?.nombreUsuario }}</p>
              </div>
            </div>
            <button @click="closePasswordModal" class="p-2 hover:bg-white/30 text-white rounded-lg transition-colors">
              <Icon icon="mdi:close" class="text-xl" />
            </button>
          </div>
        </div>
        
        <form @submit.prevent="saveCambioPassword" class="px-6 py-4">
          <div class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Nueva Contraseña <span class="text-red-500">*</span>
              </label>
              <input
                v-model="passwordForm.contrasenaNueva"
                type="password"
                required
                minlength="6"
                class="w-full px-4 py-2.5 border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 focus:outline-none transition-all duration-300 text-sm"
                placeholder="Mínimo 6 caracteres"
              />
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Confirmar Contraseña <span class="text-red-500">*</span>
              </label>
              <input
                v-model="passwordForm.confirmarContrasena"
                type="password"
                required
                minlength="6"
                class="w-full px-4 py-2.5 border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 focus:outline-none transition-all duration-300 text-sm"
                placeholder="Repite la contraseña"
              />
            </div>

            <p v-if="passwordError" class="text-xs text-red-600">{{ passwordError }}</p>
          </div>
          
          <div class="mt-6 flex justify-end space-x-3">
            <button
              type="button"
              @click="closePasswordModal"
              class="px-4 py-2.5 border-2 border-gray-300 dark:border-gray-600 rounded-xl text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-300 text-sm"
            >
              Cancelar
            </button>
            <button
              type="submit"
              :disabled="savingPassword"
              class="px-4 py-2.5 bg-gradient-to-r from-amber-600 to-orange-600 text-white rounded-xl font-medium hover:from-amber-700 hover:to-orange-700 transition-all duration-300 disabled:opacity-50 text-sm"
            >
              <Icon v-if="savingPassword" icon="mdi:loading" class="animate-spin mr-2 inline" />
              {{ savingPassword ? 'Guardando...' : 'Cambiar Contraseña' }}
            </button>
          </div>
        </form>
      </div>
    </div>
    </Teleport>

    <!-- MODAL DE REPORTE MEJORADO -->
    <Teleport to="body">
    <div v-if="showReportModal" class="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-[100] backdrop-blur-[2px]" @click.self="closeReportModal">
      <div class="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-6xl transform transition-all duration-300 scale-100 max-h-[95vh] overflow-hidden mx-auto my-auto flex flex-col">
        <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-blue-600 to-indigo-600 flex justify-between items-center">
          <div class="flex items-center text-white">
            <Icon icon="mdi:file-document" class="text-2xl mr-3" />
            <div>
              <h3 class="text-xl font-bold">{{ reporteTitulo }}</h3>
              <p class="text-xs text-blue-100">{{ reporteSubtitulo }} • {{ reporteUsuarios.length }} usuarios</p>
            </div>
          </div>
          <button @click="closeReportModal" class="p-2 hover:bg-white/30 text-white rounded-lg transition-colors">
            <Icon icon="mdi:close" class="text-2xl" />
          </button>
        </div>

        <div class="px-6 pt-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
          <div class="flex space-x-4">
            <button
              @click="reporteVista = 'lista'"
              :class="[
                'px-4 py-2 text-sm font-medium rounded-t-lg transition-colors',
                reporteVista === 'lista'
                  ? 'bg-white dark:bg-gray-900 text-blue-600 dark:text-blue-400 border-b-2 border-blue-600'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              ]"
            >
              <Icon icon="mdi:format-list-bulleted" class="mr-2 inline" />
              Vista Lista
            </button>
            <button
              @click="reporteVista = 'tarjetas'"
              :class="[
                'px-4 py-2 text-sm font-medium rounded-t-lg transition-colors',
                reporteVista === 'tarjetas'
                  ? 'bg-white dark:bg-gray-900 text-blue-600 dark:text-blue-400 border-b-2 border-blue-600'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              ]"
            >
              <Icon icon="mdi:card-multiple" class="mr-2 inline" />
              Vista Tarjetas
            </button>
          </div>
        </div>

        <div class="flex-1 overflow-y-auto p-6 bg-gray-50 dark:bg-gray-800">
          <div v-if="reporteCargando" class="flex items-center justify-center py-20">
            <div class="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
            <p class="ml-4 text-gray-600 dark:text-gray-400">Generando reporte...</p>
          </div>
          <div v-else-if="reporteUsuarios.length === 0" class="text-center py-20 text-gray-500">
            No hay usuarios para mostrar en este reporte.
          </div>
          <div v-else>
            <!-- Vista Lista -->
            <div v-if="reporteVista === 'lista'" class="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              <table class="w-full text-sm">
                <thead class="bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                  <tr>
                    <th class="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">Usuario</th>
                    <th class="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">Nombre</th>
                    <th class="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">Email</th>
                    <th class="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">Rol</th>
                    <th class="px-4 py-3 text-center text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">Estado</th>
                    <th class="px-4 py-3 text-center text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">Intentos</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
                  <tr v-for="usuario in reporteUsuarios" :key="usuario.idUsuario" class="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td class="px-4 py-3 font-mono text-xs">@{{ usuario.nombreUsuario }}</td>
                    <td class="px-4 py-3 font-medium">{{ obtenerNombreCompleto(usuario) }}</td>
                    <td class="px-4 py-3 text-xs">{{ usuario.email }}</td>
                    <td class="px-4 py-3">{{ usuario.nombreRol || '—' }}</td>
                    <td class="px-4 py-3 text-center">
                      <span :class="[
                        'px-2 py-1 text-xs font-semibold rounded-full',
                        getStatusClass(usuario.estado)
                      ]">
                        {{ convertirEstadoATexto(usuario.estado) }}
                      </span>
                    </td>
                    <td class="px-4 py-3 text-center">
                      <span :class="[
                        'px-2 py-1 text-xs font-semibold rounded-full',
                        usuario.intento_login > 3 ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
                      ]">
                        {{ usuario.intento_login || 0 }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Vista Tarjetas -->
            <div v-else class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              <div
                v-for="usuario in reporteUsuarios"
                :key="usuario.idUsuario"
                class="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-all"
              >
                <div class="flex justify-between items-start">
                  <div>
                    <p class="text-xs text-gray-500 dark:text-gray-400">@{{ usuario.nombreUsuario }}</p>
                    <h4 class="font-semibold text-gray-800 dark:white">{{ obtenerNombreCompleto(usuario) }}</h4>
                    <p class="text-xs text-gray-500 dark:text-gray-400">{{ usuario.email }}</p>
                    <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">Rol: {{ usuario.nombreRol || 'Sin rol' }}</p>
                    <span :class="[
                      'px-2 py-1 text-xs font-semibold rounded-full',
                      getStatusClass(usuario.estado)
                    ]">
                      {{ convertirEstadoATexto(usuario.estado) }}
                    </span>
                    <button @click.stop="abrirModalEliminar(usuario)" class="p-2 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-100 dark:hover:bg-red-800/50 transition-colors ml-2" title="Eliminar">
                      <Icon icon="mdi:delete" class="text-base md:text-lg" />
                    </button>
                  </div>
                </div>
                <div class="mt-3 flex justify-between items-center">
                  <div>
                    <p class="text-xs text-gray-500">Intentos</p>
                    <p :class="[
                      'font-bold',
                      usuario.intento_login > 3 ? 'text-red-600 dark:text-red-400' : 'text-gray-700 dark:text-gray-300'
                    ]">
                      {{ usuario.intento_login || 0 }}
                    </p>
                  </div>
                  <div class="text-right">
                    <p class="text-xs text-gray-500">Último acceso</p>
                    <p class="text-xs">{{ formatDate(usuario.ultimo_acceso, false) || 'Nunca' }}</p>
                  </div>
                </div>
              </div>
            </div>

            <div class="mt-4 text-right text-sm text-gray-600 dark:text-gray-400">
              Total de usuarios: <span class="font-bold">{{ reporteUsuarios.length }}</span>
            </div>
          </div>
        </div>

        <div class="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 flex justify-between items-center">
          <div class="text-xs text-gray-500">
            <Icon icon="mdi:information" class="inline mr-1" />
            Puedes cambiar entre vista lista y vista tarjetas
          </div>
          <div class="flex space-x-3">
            <button
              @click="closeReportModal"
              class="px-4 py-2 border-2 border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-300 text-sm"
            >
              Cerrar
            </button>
            <button
              @click="imprimirReporte"
              class="px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg font-medium hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 text-sm flex items-center"
            >
              <Icon icon="mdi:printer" class="mr-2 text-lg" />
              Imprimir
            </button>
          </div>
        </div>
      </div>
    </div>
    </Teleport>

    <!-- ========== MODAL DE CONFIRMACIÓN GENÉRICO ========== -->
    <Teleport to="body">
    <div v-if="showConfirmModal" class="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-[110] backdrop-blur-[2px]" @click.self="cancelarConfirmacion">
      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md transform transition-all duration-300 scale-100 mx-auto my-auto overflow-hidden">
        <div class="px-6 py-4" :class="confirmModalColor">
          <div class="flex items-center">
            <div class="p-3 rounded-full bg-white bg-opacity-30 mr-4">
              <Icon :icon="confirmModalIcon" class="text-white text-2xl" />
            </div>
            <div>
              <h3 class="text-lg font-bold text-white">{{ confirmModalTitulo }}</h3>
              <p class="text-sm text-white text-opacity-90">{{ confirmModalSubtitulo }}</p>
            </div>
          </div>
        </div>
        
        <div class="px-6 py-5">
          <p class="text-gray-700 dark:text-gray-300 mb-2">{{ confirmModalMensaje }}</p>
          <p v-if="confirmModalDetalle" class="text-sm text-gray-500 dark:text-gray-400 mb-4">{{ confirmModalDetalle }}</p>
          
          <!-- Detalles específicos según el tipo -->
          <div v-if="confirmModalTipo === 'toggle-status' && confirmModalUsuario" class="mb-4 p-3 rounded-lg" :class="getStatusDetailClass(confirmModalUsuario.estado)">
            <div class="flex items-center">
              <Icon :icon="getStatusIcon(confirmModalUsuario.estado, true)" class="text-xl mr-2" :class="getStatusTextClass(confirmModalUsuario.estado)" />
              <div>
                <p class="font-medium text-gray-900 dark:text-white">{{ obtenerNombreCompleto(confirmModalUsuario) }}</p>
                <p class="text-xs text-gray-500 dark:text-gray-400">Estado actual: {{ convertirEstadoATexto(confirmModalUsuario.estado) }}</p>
              </div>
            </div>
            <p class="mt-2 text-xs">
              El usuario pasará a estado: 
              <span class="font-semibold" :class="getNextStatusClass(confirmModalUsuario.estado)">
                {{ getNextStatus(confirmModalUsuario.estado) }}
              </span>
            </p>
          </div>

          <div v-if="confirmModalTipo === 'reset-intentos' && confirmModalUsuario" class="mb-4 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
            <div class="flex items-center">
              <Icon icon="mdi:refresh" class="text-green-600 dark:text-green-400 text-xl mr-2" />
              <div>
                <p class="font-medium text-gray-900 dark:text-white">{{ obtenerNombreCompleto(confirmModalUsuario) }}</p>
                <p class="text-xs text-gray-500 dark:text-gray-400">Intentos actuales: {{ confirmModalUsuario.intento_login }}</p>
              </div>
            </div>
          </div>

          <div v-if="confirmModalTipo === 'eliminar' && confirmModalUsuario" class="mb-4 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
            <div class="flex items-center">
              <Icon icon="mdi:alert-circle" class="text-red-600 dark:text-red-400 text-xl mr-2" />
              <div>
                <p class="font-medium text-gray-900 dark:text-white">{{ obtenerNombreCompleto(confirmModalUsuario) }}</p>
                <p class="text-xs text-gray-500 dark:text-gray-400">@{{ confirmModalUsuario.nombreUsuario }}</p>
              </div>
            </div>
            <p class="mt-2 text-xs text-red-600 dark:text-red-400">
              <Icon icon="mdi:alert" class="inline mr-1" />
              Esta acción no se puede deshacer
            </p>
          </div>
        </div>
        
        <div class="px-6 py-4 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
          <button
            @click="cancelarConfirmacion"
            class="px-4 py-2 border-2 border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-300 text-sm"
          >
            Cancelar
          </button>
          <button
            @click="ejecutarAccionConfirmada"
            class="px-4 py-2 rounded-lg text-white font-medium transition-all duration-300 text-sm flex items-center"
            :class="confirmModalButtonColor"
          >
            <Icon v-if="confirmModalCargando" icon="mdi:loading" class="animate-spin mr-2" />
            {{ confirmModalCargando ? 'Procesando...' : confirmModalBotonTexto }}
          </button>
        </div>
      </div>
    </div>
    </Teleport>
  </div>
</template>

<script>
import { Icon } from "@iconify/vue";
import { debounce } from 'lodash';
import { useToast } from "vue-toastification";
import { toTitleCase } from '@/utils/stringUtils.js';

// APIs de usuarios
import {
  obtenerUsuarios,
  obtenerUsuarioPorId,
  crearUsuario,
  actualizarUsuario,
  eliminarUsuario,
  cambiarEstadoUsuario,
  activarUsuario,
  desactivarUsuario,
  cambiarContrasena,
  resetearIntentosLogin,
  verificarUsername,
  verificarEmail,
  obtenerRoles,
  obtenerUsuariosPorRol,
  obtenerUrlImagen,
  obtenerNombreCompleto,
  obtenerIniciales,
  obtenerColorAvatar,
  convertirEstadoATexto,
  formatearTelefono,
  buscarUsuarios,
  filtrarUsuariosPorEstado,
  obtenerEstadisticas,
  subirImagen
} from '@/api/usuarios.js';

// Importar utilidades de reportes
import {
  imprimirReporteUsuarios,
  cargarLogo
} from '@/utils/reporteUsuarioGenerator.js';

export default {
  name: 'UsuariosList',
  components: {
    Icon,
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      // Datos principales
      usuarios: [],
      roles: [],
      rolesCargados: false,

      // UI States
      loading: false,
      saving: false,
      savingPassword: false,
      showModal: false,
      showPasswordModal: false,
      showReportModal: false,
      reporteCargando: false,
      reporteUsuarios: [],
      reporteTitulo: '',
      reporteSubtitulo: '',
      reporteVista: 'lista',

      // Filtros y búsqueda
      searchQuery: '',
      filterStatus: 'all',
      filterRol: 'all',
      filterTipo: 'all',
      sortBy: 'nombre',
      currentPage: 1,
      itemsPerPage: 6,
      selectedRolReporte: '',

      // Foto de perfil
      selectedFile: null,
      previewUrl: null,

      // Formularios
      form: {
        idUsuario: null,
        nombre: '',
        apellido: '',
        email: '',
        telefono: '',
        nombreUsuario: '',
        contrasena: '',
        idRol: '',
        especialidad: '',
        tipo_tecnico: false,
        comision_porcentaje: 0,
        fotoPerfil: '',
        estado: 'Activo'
      },
      passwordForm: {
        idUsuario: null,
        contrasenaNueva: '',
        confirmarContrasena: ''
      },
      passwordUsuario: null,
      passwordError: '',
      errors: {},
      editingUsuario: null,
      lastUpdate: new Date(),

      // Variables para modales de confirmación
      showConfirmModal: false,
      confirmModalTipo: '',
      confirmModalTitulo: '',
      confirmModalSubtitulo: '',
      confirmModalMensaje: '',
      confirmModalDetalle: '',
      confirmModalIcon: '',
      confirmModalColor: '',
      confirmModalButtonColor: '',
      confirmModalBotonTexto: '',
      confirmModalUsuario: null,
      confirmModalCargando: false,
      confirmModalAccion: null,
      confirmModalDatos: null,
    };
  },
  computed: {
    esTecnico() {
      return this.form.tipo_tecnico;
    },

    filteredUsuarios() {
      let filtered = [...this.usuarios];

      if (this.searchQuery.trim()) {
        filtered = buscarUsuarios(filtered, this.searchQuery);
      }

      filtered = filtrarUsuariosPorEstado(filtered, this.filterStatus);

      if (this.filterRol !== 'all') {
        filtered = filtered.filter(u => u.idRol === parseInt(this.filterRol));
      }

      if (this.filterTipo === 'tecnicos') {
        filtered = filtered.filter(u => u.tipo_tecnico === true);
      } else if (this.filterTipo === 'no_tecnicos') {
        filtered = filtered.filter(u => u.tipo_tecnico !== true);
      }

      filtered = this.ordenarUsuarios(filtered, this.sortBy);

      return filtered;
    },

    estadisticas() {
      return obtenerEstadisticas(this.usuarios);
    },

    paginatedUsuarios() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredUsuarios.slice(start, end);
    },

    totalPages() {
      return Math.ceil(this.filteredUsuarios.length / this.itemsPerPage);
    },

    visiblePages() {
      const pages = [];
      const maxVisible = 5;
      let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
      let end = Math.min(this.totalPages, start + maxVisible - 1);

      if (end - start + 1 < maxVisible) {
        start = Math.max(1, end - maxVisible + 1);
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    },

    startItem() {
      return (this.currentPage - 1) * this.itemsPerPage + 1;
    },

    endItem() {
      return Math.min(this.currentPage * this.itemsPerPage, this.filteredUsuarios.length);
    },

    isFormValid() {
      const baseValid = this.form.nombre.trim() && 
             this.form.apellido.trim() && 
             this.form.email.trim() && 
             this.form.nombreUsuario.trim() && 
             this.form.idRol;
      
      if (!this.editingUsuario) {
        return baseValid && this.form.contrasena && this.form.contrasena.length >= 6;
      }
      
      return baseValid;
    },
  },
  watch: {
    filterStatus() {
      this.currentPage = 1;
    },
    filterRol() {
      this.currentPage = 1;
    },
    filterTipo() {
      this.currentPage = 1;
    },
    searchQuery() {
      this.currentPage = 1;
    },
    sortBy() {
      this.currentPage = 1;
    },
  },
  async mounted() {
    await this.cargarRoles();
    await this.loadData();
  },
  methods: {
    toTitleCase,
    
    // ========== MÉTODOS DE CARGA DE DATOS ==========
    async cargarRoles() {
      try {
        const response = await obtenerRoles();
        if (response.success) {
          this.roles = response.data || [];
          this.rolesCargados = true;
        }
      } catch (error) {
        console.error('Error cargando roles:', error);
        this.mostrarError('Error al cargar los roles');
      }
    },

    async loadData() {
      this.loading = true;
      try {
        const response = await obtenerUsuarios(this.filterStatus);
        
        if (response.success) {
          this.usuarios = response.data || [];
          this.lastUpdate = new Date();
        }

        if (!this.rolesCargados) {
          await this.cargarRoles();
        }

      } catch (error) {
        console.error('Error al cargar datos:', error);
        this.mostrarError('Error al cargar los datos');
      } finally {
        this.loading = false;
      }
    },

    handleFilterChange() {
      this.currentPage = 1;
      this.loadData();
    },

    handleSearch: debounce(function() {
      this.currentPage = 1;
    }, 300),

    ordenarUsuarios(usuarios, criterio) {
      const sorted = [...usuarios];
      
      switch (criterio) {
        case 'nombre':
          sorted.sort((a, b) => obtenerNombreCompleto(a).localeCompare(obtenerNombreCompleto(b)));
          break;
        case 'nombre_desc':
          sorted.sort((a, b) => obtenerNombreCompleto(b).localeCompare(obtenerNombreCompleto(a)));
          break;
        case 'nombreUsuario':
          sorted.sort((a, b) => (a.nombreUsuario || '').localeCompare(b.nombreUsuario || ''));
          break;
        case 'email':
          sorted.sort((a, b) => (a.email || '').localeCompare(b.email || ''));
          break;
        case 'rol':
          sorted.sort((a, b) => (a.nombreRol || '').localeCompare(b.nombreRol || ''));
          break;
        case 'fechaRegistro_desc':
          sorted.sort((a, b) => new Date(b.fechaRegistro) - new Date(a.fechaRegistro));
          break;
        case 'intentos_desc':
          sorted.sort((a, b) => (b.intento_login || 0) - (a.intento_login || 0));
          break;
        default:
          break;
      }
      
      return sorted;
    },

    // ========== MÉTODOS DE RED ==========
    getImageUrl(imagen) {
      return obtenerUrlImagen(imagen);
    },

    // ========== MÉTODOS DE TOAST ==========
    mostrarExito(mensaje) {
      this.toast.success(mensaje, { timeout: 3000 });
    },

    mostrarError(mensaje) {
      this.toast.error(mensaje, { timeout: 5000 });
    },

    mostrarAdvertencia(mensaje) {
      this.toast.warning(mensaje, { timeout: 4000 });
    },

    // ========== MÉTODOS DE IMAGEN ==========
    handleImageError(event) {
      console.warn('Error cargando imagen:', event.target.src);
      event.target.style.display = 'none';
    },

    triggerFileInput() {
      this.$refs.fileInput.click();
    },

    handleFileChange(event) {
      const file = event.target.files[0];
      if (!file) return;

      if (file.size > 5 * 1024 * 1024) {
        this.mostrarError('La imagen no puede superar los 5MB');
        return;
      }

      if (!file.type.startsWith('image/')) {
        this.mostrarError('El archivo debe ser una imagen');
        return;
      }

      this.selectedFile = file;
      this.previewUrl = URL.createObjectURL(file);
    },

    removeSelectedFile() {
      this.selectedFile = null;
      this.previewUrl = null;
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },

    // ========== MÉTODOS DE FORMATEO ==========
    obtenerNombreCompleto(usuario) {
      return obtenerNombreCompleto(usuario);
    },

    obtenerIniciales(usuario) {
      return obtenerIniciales(usuario);
    },

    obtenerColorAvatar(texto) {
      return obtenerColorAvatar(texto);
    },

    convertirEstadoATexto(estado) {
      return convertirEstadoATexto(estado);
    },

    formatearTelefono(telefono) {
      return formatearTelefono(telefono);
    },

    getStatusClass(estado) {
      const estadoLower = (estado || '').toLowerCase();
      switch (estadoLower) {
        case 'activo':
          return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
        case 'inactivo':
          return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
        case 'bloqueado':
          return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
        default:
          return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
      }
    },

    getStatusIcon(estado) {
      const estadoLower = (estado || '').toLowerCase();
      switch (estadoLower) {
        case 'activo':
          return 'mdi:check-circle';
        case 'inactivo':
          return 'mdi:close-circle';
        case 'bloqueado':
          return 'mdi:block-helper';
        default:
          return 'mdi:help-circle';
      }
    },

    getStatusDetailClass(estado) {
      const estadoLower = (estado || '').toLowerCase();
      switch (estadoLower) {
        case 'activo':
          return 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800';
        case 'inactivo':
          return 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800';
        case 'bloqueado':
          return 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800';
        default:
          return 'bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700';
      }
    },

    getStatusTextClass(estado) {
      const estadoLower = (estado || '').toLowerCase();
      switch (estadoLower) {
        case 'activo':
          return 'text-red-600 dark:text-red-400';
        case 'inactivo':
          return 'text-green-600 dark:text-green-400';
        case 'bloqueado':
          return 'text-green-600 dark:text-green-400';
        default:
          return 'text-gray-600 dark:text-gray-400';
      }
    },

    getNextStatus(estadoActual) {
      const estadoLower = (estadoActual || '').toLowerCase();
      if (estadoLower === 'activo') return 'Inactivo';
      if (estadoLower === 'inactivo') return 'Activo';
      if (estadoLower === 'bloqueado') return 'Activo';
      return 'Activo';
    },

    getNextStatusClass(estadoActual) {
      const estadoLower = (estadoActual || '').toLowerCase();
      if (estadoLower === 'activo') return 'text-red-600 dark:text-red-400';
      return 'text-green-600 dark:text-green-400';
    },

    formatDate(fechaISO, completo = true) {
      if (!fechaISO) return '';
      try {
        const fecha = new Date(fechaISO);
        const opciones = {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        };
        if (completo) {
          opciones.hour = '2-digit';
          opciones.minute = '2-digit';
        }
        return fecha.toLocaleDateString('es-VE', opciones);
      } catch (error) {
        return fechaISO;
      }
    },

    formatTime(fechaISO) {
      if (!fechaISO) return '';
      try {
        const fecha = new Date(fechaISO);
        return fecha.toLocaleTimeString('es-VE', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        });
      } catch (error) {
        return '';
      }
    },

    // ========== MÉTODOS DE CONFIRMACIÓN MODAL ==========
    abrirModalConfirmacion(config) {
      this.confirmModalTipo = config.tipo;
      this.confirmModalTitulo = config.titulo;
      this.confirmModalSubtitulo = config.subtitulo || '';
      this.confirmModalMensaje = config.mensaje;
      this.confirmModalDetalle = config.detalle || '';
      this.confirmModalIcon = config.icon || 'mdi:help-circle';
      this.confirmModalColor = config.color || 'bg-gradient-to-r from-blue-600 to-indigo-600';
      this.confirmModalButtonColor = config.buttonColor || 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700';
      this.confirmModalBotonTexto = config.botonTexto || 'Confirmar';
      this.confirmModalUsuario = config.usuario || null;
      this.confirmModalAccion = config.accion;
      this.confirmModalDatos = config.datos || null;
      this.showConfirmModal = true;
    },

    cancelarConfirmacion() {
      this.showConfirmModal = false;
      this.confirmModalCargando = false;
      this.confirmModalUsuario = null;
      this.confirmModalAccion = null;
      this.confirmModalDatos = null;
    },

    async ejecutarAccionConfirmada() {
      if (this.confirmModalAccion) {
        this.confirmModalCargando = true;
        await this.confirmModalAccion(this.confirmModalDatos);
        this.confirmModalCargando = false;
        this.cancelarConfirmacion();
      }
    },

    // ========== MÉTODOS DE CONFIRMACIÓN ESPECÍFICOS ==========
    abrirModalEliminar(usuario) {
      this.abrirModalConfirmacion({
        tipo: 'eliminar',
        titulo: 'Eliminar Usuario',
        subtitulo: 'Esta acción no se puede deshacer',
        mensaje: `¿Estás seguro de eliminar a "${obtenerNombreCompleto(usuario)}"?`,
        detalle: 'El usuario será eliminado permanentemente del sistema.',
        icon: 'mdi:alert-circle',
        color: 'bg-gradient-to-r from-red-600 to-pink-600',
        buttonColor: 'bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-700 hover:to-pink-700',
        botonTexto: 'Eliminar',
        usuario: usuario,
        accion: () => this.deleteUsuario(usuario.idUsuario)
      });
    },

    abrirModalToggleStatus(usuario) {
      const esActivo = usuario.estado?.toLowerCase() === 'activo';
      let titulo, mensaje, nuevoEstado;
      
      if (esActivo) {
        titulo = 'Desactivar Usuario';
        mensaje = `¿Estás seguro de desactivar a "${obtenerNombreCompleto(usuario)}"?`;
        nuevoEstado = 'Inactivo';
      } else {
        titulo = 'Activar Usuario';
        mensaje = `¿Estás seguro de activar a "${obtenerNombreCompleto(usuario)}"?`;
        nuevoEstado = 'Activo';
      }
      
      this.abrirModalConfirmacion({
        tipo: 'toggle-status',
        titulo: titulo,
        subtitulo: `Cambiar estado del usuario`,
        mensaje: mensaje,
        detalle: `El usuario pasará de "${usuario.estado}" a "${nuevoEstado}".`,
        icon: esActivo ? 'mdi:close-circle' : 'mdi:check-circle',
        color: esActivo ? 'bg-gradient-to-r from-red-600 to-orange-600' : 'bg-gradient-to-r from-green-600 to-emerald-600',
        buttonColor: esActivo ? 'bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700' : 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700',
        botonTexto: esActivo ? 'Desactivar' : 'Activar',
        usuario: usuario,
        accion: () => this.toggleStatus(usuario, nuevoEstado)
      });
    },

    abrirModalResetearIntentos(usuario) {
      this.abrirModalConfirmacion({
        tipo: 'reset-intentos',
        titulo: 'Resetear Intentos',
        subtitulo: 'Restablecer contador de intentos fallidos',
        mensaje: `¿Resetear los intentos de login de "${obtenerNombreCompleto(usuario)}"?`,
        detalle: `Actualmente tiene ${usuario.intento_login} intento(s) fallido(s).`,
        icon: 'mdi:refresh',
        color: 'bg-gradient-to-r from-green-600 to-emerald-600',
        buttonColor: 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700',
        botonTexto: 'Resetear',
        usuario: usuario,
        accion: () => this.resetIntentos(usuario.idUsuario)
      });
    },

    // ========== MÉTODOS DE REPORTES ==========
    generarReportePorFiltro(tipoFiltro, tituloPersonalizado = null) {
      let productosFiltrados = filtrarUsuariosPorEstado(this.usuarios, tipoFiltro);
      
      if (tipoFiltro === 'tecnicos') {
        productosFiltrados = this.usuarios.filter(u => u.tipo_tecnico === true);
      } else if (tipoFiltro === 'con_intentos') {
        productosFiltrados = this.usuarios.filter(u => u.intento_login > 0);
      }
      
      if (productosFiltrados.length === 0) {
        this.toast.warning('No hay usuarios que coincidan con este filtro');
        return;
      }
      
      let titulo = tituloPersonalizado;
      if (!titulo) {
        switch (tipoFiltro) {
          case 'activos': titulo = 'USUARIOS ACTIVOS'; break;
          case 'inactivos': titulo = 'USUARIOS INACTIVOS'; break;
          case 'bloqueados': titulo = 'USUARIOS BLOQUEADOS'; break;
          case 'tecnicos': titulo = 'TÉCNICOS'; break;
          case 'con_intentos': titulo = 'USUARIOS CON INTENTOS FALLIDOS'; break;
          default: titulo = 'REPORTE DE USUARIOS';
        }
      }
      
      let subtitulo = '';
      switch (tipoFiltro) {
        case 'activos': subtitulo = 'Solo usuarios activos'; break;
        case 'inactivos': subtitulo = 'Solo usuarios inactivos'; break;
        case 'bloqueados': subtitulo = 'Solo usuarios bloqueados'; break;
        case 'tecnicos': subtitulo = 'Usuarios con rol técnico'; break;
        case 'con_intentos': subtitulo = 'Usuarios con intentos de login fallidos'; break;
      }
      
      this.reporteTitulo = titulo;
      this.reporteSubtitulo = subtitulo;
      this.reporteUsuarios = productosFiltrados;
      this.reporteVista = 'lista';
      this.showReportModal = true;
    },
    
    generarReporteGeneral() {
      this.reporteTitulo = 'REPORTE GENERAL DE USUARIOS';
      this.reporteSubtitulo = `Todos los usuarios (${this.usuarios.length})`;
      this.reporteUsuarios = this.usuarios;
      this.reporteVista = 'lista';
      this.showReportModal = true;
    },
    
    generarReportePorRol() {
      if (!this.selectedRolReporte) return;
      
      const rol = this.roles.find(r => r.idRol === parseInt(this.selectedRolReporte));
      if (!rol) return;
      
      const usuariosFiltrados = this.usuarios.filter(u => u.idRol === parseInt(this.selectedRolReporte));
      
      if (usuariosFiltrados.length === 0) {
        this.toast.warning('No hay usuarios en este rol');
        return;
      }
      
      this.reporteTitulo = `USUARIOS: ${rol.nombre.toUpperCase()}`;
      this.reporteSubtitulo = `Rol: ${rol.nombre}`;
      this.reporteUsuarios = usuariosFiltrados;
      this.reporteVista = 'lista';
      this.showReportModal = true;
      
      this.selectedRolReporte = '';
    },
    
    async imprimirReporte() {
      try {
        let filtro = 'todos';
        if (this.reporteTitulo.includes('ACTIVOS')) filtro = 'activos';
        else if (this.reporteTitulo.includes('INACTIVOS')) filtro = 'inactivos';
        else if (this.reporteTitulo.includes('BLOQUEADOS')) filtro = 'bloqueados';
        else if (this.reporteTitulo.includes('TÉCNICOS')) filtro = 'tecnicos';
        
        await imprimirReporteUsuarios(
          this.reporteUsuarios,
          this.reporteTitulo,
          filtro
        );
        
      } catch (error) {
        console.error('Error al imprimir reporte:', error);
        this.toast.error('Error al generar el reporte');
      }
    },
    
    closeReportModal() {
      this.showReportModal = false;
      this.reporteUsuarios = [];
      this.reporteVista = 'lista';
    },

    // ========== MÉTODOS DEL MODAL ==========
    openCreateModal() {
      this.editingUsuario = null;
      this.resetForm();
      this.errors = {};
      this.showModal = true;
    },

    openEditModal(usuario) {
      this.editingUsuario = usuario;
      this.form = {
        idUsuario: usuario.idUsuario,
        nombre: usuario.nombre || '',
        apellido: usuario.apellido || '',
        email: usuario.email || '',
        telefono: usuario.telefono || '',
        nombreUsuario: usuario.nombreUsuario || '',
        contrasena: '',
        idRol: usuario.idRol || '',
        especialidad: usuario.especialidad || '',
        tipo_tecnico: usuario.tipo_tecnico || false,
        comision_porcentaje: usuario.comision_porcentaje || 0,
        fotoPerfil: usuario.fotoPerfil || '',
        estado: usuario.estado || 'Activo'
      };
      
      if (usuario.fotoPerfil) {
        this.previewUrl = this.getImageUrl(usuario.fotoPerfil);
      } else {
        this.previewUrl = null;
      }
      this.selectedFile = null;
      
      this.errors = {};
      this.showModal = true;
    },

    closeModal() {
      this.showModal = false;
      this.resetForm();
      this.errors = {};
      this.selectedFile = null;
      this.previewUrl = null;
    },

    resetForm() {
      this.form = {
        idUsuario: null,
        nombre: '',
        apellido: '',
        email: '',
        telefono: '',
        nombreUsuario: '',
        contrasena: '',
        idRol: '',
        especialidad: '',
        tipo_tecnico: false,
        comision_porcentaje: 0,
        fotoPerfil: '',
        estado: 'Activo'
      };
      this.editingUsuario = null;
      this.selectedFile = null;
      this.previewUrl = null;
    },

    abrirModalCambiarPassword(usuario) {
      this.passwordUsuario = usuario;
      this.passwordForm = {
        idUsuario: usuario.idUsuario,
        contrasenaNueva: '',
        confirmarContrasena: ''
      };
      this.passwordError = '';
      this.showPasswordModal = true;
    },

    closePasswordModal() {
      this.showPasswordModal = false;
      this.passwordUsuario = null;
      this.passwordForm = {
        idUsuario: null,
        contrasenaNueva: '',
        confirmarContrasena: ''
      };
      this.passwordError = '';
    },

    // ========== VALIDACIONES ==========
    async verificarUsernameUnico() {
      if (!this.form.nombreUsuario.trim() || this.editingUsuario?.nombreUsuario === this.form.nombreUsuario) {
        return;
      }

      try {
        const response = await verificarUsername(this.form.nombreUsuario.trim());
        if (response.success && response.existe) {
          this.errors.nombreUsuario = 'El nombre de usuario ya existe';
        } else {
          delete this.errors.nombreUsuario;
        }
      } catch (error) {
        console.error('Error verificando username:', error);
      }
    },

    async verificarEmailUnico() {
      if (!this.form.email.trim() || this.editingUsuario?.email === this.form.email) {
        return;
      }

      try {
        const response = await verificarEmail(this.form.email.trim());
        if (response.success && response.existe) {
          this.errors.email = 'El email ya existe';
        } else {
          delete this.errors.email;
        }
      } catch (error) {
        console.error('Error verificando email:', error);
      }
    },

    validateForm() {
      this.errors = {};

      if (!this.form.nombre.trim()) {
        this.errors.nombre = 'El nombre es requerido';
      }

      if (!this.form.apellido.trim()) {
        this.errors.apellido = 'El apellido es requerido';
      }

      if (!this.form.email.trim()) {
        this.errors.email = 'El email es requerido';
      } else if (!/^\S+@\S+\.\S+$/.test(this.form.email)) {
        this.errors.email = 'Email inválido';
      }

      if (!this.form.nombreUsuario.trim()) {
        this.errors.nombreUsuario = 'El nombre de usuario es requerido';
      }

      if (!this.form.idRol) {
        this.errors.idRol = 'El rol es requerido';
      }

      if (!this.editingUsuario && (!this.form.contrasena || this.form.contrasena.length < 6)) {
        this.errors.contrasena = 'La contraseña debe tener al menos 6 caracteres';
      }

      return Object.keys(this.errors).length === 0;
    },

    // ========== MÉTODOS CRUD ==========
    async saveUsuario() {
      if (!this.validateForm()) return;

      this.saving = true;
      try {
        const usuarioData = {
          nombre: this.form.nombre.trim(),
          apellido: this.form.apellido.trim(),
          email: this.form.email.trim(),
          telefono: this.form.telefono?.trim() || null,
          nombreUsuario: this.form.nombreUsuario.trim(),
          idRol: parseInt(this.form.idRol),
          especialidad: this.form.especialidad?.trim() || null,
          tipo_tecnico: this.form.tipo_tecnico,
          comision_porcentaje: parseFloat(this.form.comision_porcentaje || 0),
          fotoPerfil: this.form.fotoPerfil || null,
          estado: this.form.estado
        };

        if (!this.editingUsuario) {
          usuarioData.contrasena = this.form.contrasena;
        }

        let result;
        if (this.editingUsuario) {
          result = await actualizarUsuario(this.editingUsuario.idUsuario, usuarioData);
        } else {
          result = await crearUsuario(usuarioData);
        }

        if (result.success) {
          if (this.selectedFile) {
            const idParaFoto = result.data?.idUsuario || this.editingUsuario?.idUsuario;
            if (idParaFoto) {
              await subirImagen(this.selectedFile, idParaFoto, 'usuario');
            }
          }

          await this.loadData();
          this.closeModal();
          this.mostrarExito(this.editingUsuario ? '¡Usuario actualizado!' : '¡Usuario creado!');
        } else {
          throw new Error(result.error || 'Error al guardar el usuario');
        }
      } catch (error) {
        console.error('Error al guardar usuario:', error);
        this.mostrarError(error.message || 'Error al guardar el usuario');
      } finally {
        this.saving = false;
      }
    },

    async deleteUsuario(id) {
      try {
        const result = await eliminarUsuario(id);
        if (result.success) {
          await this.loadData();
          this.mostrarExito('¡Usuario eliminado exitosamente!');
        } else {
          throw new Error(result.error || 'Error al eliminar el usuario');
        }
      } catch (error) {
        console.error('Error al eliminar usuario:', error);
        this.mostrarError(error.message || 'Error al eliminar el usuario');
      }
    },

    async toggleStatus(usuario, nuevoEstado) {
      try {
        let toggleResult;
        
        // Usar funciones específicas según el nuevo estado
        if (nuevoEstado.toLowerCase() === 'activo') {
          toggleResult = await activarUsuario(usuario.idUsuario);
        } else if (nuevoEstado.toLowerCase() === 'inactivo' || nuevoEstado.toLowerCase() === 'bloqueado') {
          toggleResult = await desactivarUsuario(usuario.idUsuario);
        } else {
          toggleResult = await cambiarEstadoUsuario({
            idUsuario: usuario.idUsuario,
            estado: nuevoEstado
          });
        }

        if (toggleResult.success) {
          await this.loadData();
          this.mostrarExito(`¡Usuario ${nuevoEstado.toLowerCase()} exitosamente!`);
        } else {
          throw new Error(toggleResult.error || 'Error al cambiar el estado');
        }
      } catch (error) {
        console.error('Error al cambiar estado:', error);
        this.mostrarError(error.message || 'Error al cambiar el estado');
      }
    },

    async saveCambioPassword() {
      if (this.passwordForm.contrasenaNueva !== this.passwordForm.confirmarContrasena) {
        this.passwordError = 'Las contraseñas no coinciden';
        return;
      }

      if (this.passwordForm.contrasenaNueva.length < 6) {
        this.passwordError = 'La contraseña debe tener al menos 6 caracteres';
        return;
      }

      this.savingPassword = true;
      this.passwordError = '';

      try {
        const result = await cambiarContrasena({
          idUsuario: this.passwordForm.idUsuario,
          contrasenaActual: '',
          nuevaContrasena: this.passwordForm.contrasenaNueva,
          confirmarContrasena: this.passwordForm.confirmarContrasena
        });

        if (result.success) {
          this.closePasswordModal();
          this.mostrarExito('¡Contraseña cambiada exitosamente!');
        } else {
          throw new Error(result.error || 'Error al cambiar la contraseña');
        }
      } catch (error) {
        console.error('Error cambiando contraseña:', error);
        this.passwordError = error.message || 'Error al cambiar la contraseña';
      } finally {
        this.savingPassword = false;
      }
    },

    async resetIntentos(idUsuario) {
      try {
        const result = await resetearIntentosLogin(idUsuario);
        if (result.success) {
          await this.loadData();
          this.mostrarExito('¡Intentos reseteados exitosamente!');
        } else {
          throw new Error(result.error || 'Error al resetear intentos');
        }
      } catch (error) {
        console.error('Error reseteando intentos:', error);
        this.mostrarError('Error al resetear los intentos');
      }
    },

    clearFilters() {
      this.searchQuery = '';
      this.filterStatus = 'all';
      this.filterRol = 'all';
      this.filterTipo = 'all';
      this.sortBy = 'nombre';
      this.currentPage = 1;
      this.loadData();
    },
  },
};
</script>

<style scoped>
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}

.fade-in {
  animation: fadeIn 0.3s ease-out;
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.dark ::-webkit-scrollbar-track {
  background: #374151;
}

.dark ::-webkit-scrollbar-thumb {
  background: #6b7280;
}

.dark ::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}

@media (max-width: 768px) {
  button {
    min-height: 44px;
  }
  
  input, select, textarea {
    font-size: 16px !important;
  }
}
</style>